// src/components/antenna/antenna-snapshot-analysis.tsx
import React from "react";
import {
  ChevronDown,
  ChevronUp,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
} from "lucide-react";
import { ORB_GRADIENT } from "../cognition/constants";
import { ClickableMetric } from "../clickable-metric";
import { MetricTerm } from "../metric-term";
import { MetricInsightChips } from "../metric-insight-chips";
import { ModelFilterTabs } from "../model-filter-tabs";
import { TooltipIcon } from "../tooltip-icon";
import { metricExplanations } from "../../data/antenna-metric-explanations";

import { normalizeProviderKey } from "@/app/constants/models";
import { stripCitations } from "@/app/utils/strip-citations";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface InsightChip {
  question: string;
  answer: string;
}

interface SnapshotTheme {
  theme: string;
  sentiment: "positive" | "negative" | "neutral";
  impactScore: number;
  description: string;
}

interface SnapshotDriver {
  text: string;
  type: "strength" | "risk";
}

export interface SnapshotAnalysisData {
  executiveSummary: string;
  snapshotScore: {
    current: number;
    previous: number;
    delta: number;
  };
  detectedThemes: SnapshotTheme[];
  keyDrivers: SnapshotDriver[];
  insights: InsightChip[];
}

interface AntennaSnapshotAnalysisProps {
  /** Combined/merged data (shown when selectedModel === "all") */
  data: SnapshotAnalysisData;
  /** Per-model snapshot dict keyed by provider ("openai", "claude", etc.) */
  snapshotByModel?: Record<string, SnapshotAnalysisData>;
  /** Model tabs — same shape used across the dashboard */
  modelTabs?: { value: string; label: string }[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
  onOpenAIChat: (msg: string) => void;
  /** Greeting name. Hardcoded fallback for now (matches Deep Signal); wire the real auth name later. */
  userName?: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getSentimentColor(sentiment: string): string {
  switch (sentiment) {
    case "positive": return "#00c9c4";
    case "negative": return "var(--error-text)";
    default:         return "#28a9d2";
  }
}

/** Model source badge shown next to section titles when a specific model is selected. */
function ModelBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        padding:         "2px 8px",
        borderRadius:    9999,
        backgroundColor: "var(--alpha-teal-10)",
        border:          "1px solid var(--alpha-teal-20)",
        color:           "var(--alpha-teal)",
        fontSize:        "var(--text-xs)",
        fontWeight:      600,
        marginLeft:      8,
        flexShrink:      0,
      }}
    >
      {label}
    </span>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const DRIVER_PREVIEW = 3;
const THEME_PREVIEW  = 3;

export function AntennaSnapshotAnalysis({
  data,
  snapshotByModel = {},
  modelTabs = [],
  selectedModel = "all",
  onModelChange,
  isExpanded,
  toggleExpanded,
  onOpenAIChat,
  userName,
}: AntennaSnapshotAnalysisProps) {
  const isAll = selectedModel === "all";

  const [strengthsOpen, setStrengthsOpen] = React.useState(false);
  const [risksOpen,     setRisksOpen]     = React.useState(false);
  const [themesOpen,    setThemesOpen]    = React.useState(false);

  // Resolve which data to display
  const activeData: SnapshotAnalysisData = React.useMemo(() => {
    if (isAll) return data;
    const key = normalizeProviderKey(selectedModel);
    return snapshotByModel[key] ?? data;
  }, [isAll, selectedModel, snapshotByModel, data]);

  // Label for model badge
  const modelLabel = isAll
    ? null
    : (modelTabs.find((t) => t.value === selectedModel)?.label ?? selectedModel);

  // Whether we have per-model data to show tabs for
  const hasPerModelData = Object.keys(snapshotByModel).length > 0 && modelTabs.length > 0;

  return (
    <div className="upm-card">
      <div className="p-8 space-y-6">

        {/* ── Collapsible Header ─────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={toggleExpanded}
          style={{ userSelect: "none" }}
        >
          <div className="flex items-start gap-4">
            <div>
              <h3 style={{ fontSize: "var(--text-xl)", fontWeight: "var(--font-weight-semibold)" }}>
                {userName ? `Hello ${userName}, here is your AI briefing.` : "Here is your AI briefing."}
              </h3>
              <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
                A point-in-time read of how AI narrates your brand
              </p>
            </div>
          </div>
          <div className="flex items-center" style={{ gap: 12, flexShrink: 0 }}>
            {hasPerModelData && (
              <ModelFilterTabs
                models={modelTabs}
                selectedModel={selectedModel}
                onModelChange={onModelChange ?? (() => {})}
                combinedLabel="All Models"
                includeCombined={true}
              />
            )}
            {isExpanded
              ? <ChevronUp   className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
              : <ChevronDown className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />}
          </div>
        </div>

        {isExpanded && (
          <div className="space-y-8">

            {/* ── Executive Summary ───────────────────────────────────── */}
            {(() => {
              const summary = stripCitations(activeData.executiveSummary || "");
              // Split into lede (first sentence) and rest. The boundary may carry
              // trailing citation brackets like ".[7][18]" before the whitespace,
              // so match those too; otherwise a summary whose first sentence ends
              // in a citation never splits and the whole text becomes the lede.
              const boundary = summary.match(/[.!?](?:\s*\[\d+\])*\s/);
              const splitAt = boundary && boundary.index !== undefined && boundary.index > 0
                ? boundary.index + boundary[0].length
                : -1;
              const lede = splitAt > 0 ? summary.slice(0, splitAt).trim() : summary;
              const rest = splitAt > 0 ? summary.slice(splitAt).trim() : "";
              return (
                <div
                  style={{
                    backgroundColor: "var(--muted)",
                    border: "1px solid var(--border)",
                    borderRadius: "var(--radius-lg)",
                    padding: "24px 26px",
                  }}
                >
                  <style>{`@keyframes signalOrbBreath{0%,100%{box-shadow:var(--shadow-spectral-violet-glow)}50%{box-shadow:0 0 0 4px var(--spectral-violet-10)}}@media (prefers-reduced-motion: no-preference){.signal-analyst-orb{animation:signalOrbBreath 3.4s ease-in-out infinite}}`}</style>

                  {/* Identity header: Signal Analyst */}
                  <div className="flex items-start gap-3 mb-4">
                    <div
                      className="signal-analyst-orb"
                      style={{
                        width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                        background: ORB_GRADIENT,
                        boxShadow: "var(--shadow-spectral-violet-glow)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                      }}
                    >
                      <Sparkles style={{ width: 17, height: 17, color: "#fff" }} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div className="flex items-center gap-2" style={{ flexWrap: "wrap", rowGap: 4 }}>
                        <span style={{ fontSize: "var(--text-sm)", fontWeight: 600, color: "var(--foreground)" }}>
                          Signal Analyst
                        </span>
                        <span
                          style={{
                            fontSize: "var(--text-xs)", fontWeight: 600, textTransform: "uppercase",
                            letterSpacing: ".05em", color: "var(--spectral-violet)",
                            background: "var(--spectral-violet-10)", borderRadius: 9999, padding: "2px 7px",
                          }}
                        >
                          Executive Summary
                        </span>
                        {modelLabel && <ModelBadge label={modelLabel} />}
                      </div>
                      <div style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", marginTop: 2 }}>
                        {modelTabs.length > 0 ? `Synthesized from ${modelTabs.length} models · this run` : "This run"}
                      </div>
                    </div>
                  </div>

                  {summary ? (
                    <>
                      {/* Lede as pull-quote with gradient rule */}
                      <div style={{ display: "flex", alignItems: "stretch", gap: 14, marginBottom: rest ? 14 : 0 }}>
                        <div style={{ width: 3, borderRadius: 2, background: ORB_GRADIENT, flexShrink: 0 }} />
                        <p style={{ fontSize: "var(--text-xl)", fontWeight: 600, lineHeight: 1.45, letterSpacing: "-.01em", color: "var(--foreground)", textWrap: "pretty" } as React.CSSProperties}>
                          {lede}
                        </p>
                      </div>

                      {rest && (
                        <>
                          <div style={{ height: 1, background: "var(--border)", margin: "14px 0" }} />
                          <p style={{ fontSize: "var(--text-sm)", lineHeight: "1.75", color: "var(--muted-foreground)" }}>
                            {rest}
                          </p>
                        </>
                      )}
                    </>
                  ) : (
                    <p style={{ fontSize: "var(--text-sm)", lineHeight: "1.6", color: "var(--muted-foreground)" }}>
                      No summary available for this run yet. Run a new scan to generate one.
                    </p>
                  )}

                  {!isAll && (
                    <p
                      className="mt-4"
                      style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", opacity: 0.7 }}
                    >
                      Showing {modelLabel}'s analysis only. Switch to "All Models" for the merged view.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Key Drivers + Detected Themes now live in the Brand card
                ("How AI Describes Your Brand"). The page-level Ask Upmode bar
                (below this card) is the assistant entry point, replacing the
                old footer insight chips. */}

          </div>
        )}
      </div>
    </div>
  );
}